import { Card, CardContent } from "@/components/ui/card";
import { ProductTable } from "./ProductTable";
import { useState, useEffect } from "react";
import { AddProductDialog } from "./AddProductDialog";
import { DeleteProductDialog } from "./DeleteProductDialog";
import { DialogManager } from "./DialogManager";
import { FeatureCards } from "./FeatureCards";
import { InterfaceHeader } from "./InterfaceHeader";
import { ProductActionsSection } from "./ProductActionsSection";
import { Button } from "../ui/button";
import { useProductManagement } from "@/hooks/useProductManagement";
import { useRealtimeUpdates } from "@/hooks/useRealtimeUpdates";

interface InterfaceModelContentProps {
  interfaceModel: any;
  sampleData: any[];
  onSaleClick: (item: any, isCredit?: boolean) => void;
  onRestock: (item: any) => void;
  onLoss: (item: any) => void;
  onDelete: (item: any) => void;
  onReturn: (item: any) => void;
  businessTypeId?: string;
}

export const InterfaceModelContent = ({
  interfaceModel,
  sampleData,
  onSaleClick,
  onRestock,
  onLoss,
  onDelete,
  onReturn,
  businessTypeId,
}: InterfaceModelContentProps) => {
  const [isCredit, setIsCredit] = useState(false);
  const [localData, setLocalData] = useState(sampleData);
  
  const {
    isAddProductDialogOpen,
    setIsAddProductDialogOpen,
    isDeleteProductDialogOpen,
    setIsDeleteProductDialogOpen,
    handleAddProduct,
    handleDeleteProduct
  } = useProductManagement(() => {});

  useEffect(() => {
    if (JSON.stringify(localData) !== JSON.stringify(sampleData)) {
      setLocalData(sampleData);
    }
  }, [sampleData]);

  // Utiliser le hook de mise à jour en temps réel
  useRealtimeUpdates(localData[0]?.stock_id, (updatedData) => {
    setLocalData(updatedData);
    onDelete({ id: 'dummy', newData: updatedData });
  });

  const handleSetAsDefault = async () => {
    if (!businessTypeId || !interfaceModel?.id) return;

    try {
      const { error } = await supabase
        .from('business_types')
        .update({ default_interface_model_id: interfaceModel.id })
        .eq('id', businessTypeId);

      if (error) throw error;
      toast.success("Ce modèle est maintenant le modèle par défaut pour ce type d'entreprise");
    } catch (error) {
      console.error("Erreur lors de la définition du modèle par défaut:", error);
      toast.error("Erreur lors de la mise à jour du modèle par défaut");
    }
  };

  const commerceLayout = {
    sections: [
      {
        name: "Gestion des Ventes",
        features: [
          {
            name: "Ventes Directes",
            description: "Gérez vos ventes au comptant"
          },
          {
            name: "Ventes à Crédit",
            description: "Gérez vos ventes à crédit avec suivi des paiements"
          }
        ]
      },
      {
        name: "Gestion des Stocks",
        features: [
          {
            name: "Inventaire",
            description: "Suivez votre stock en temps réel"
          },
          {
            name: "Approvisionnements",
            description: "Gérez vos approvisionnements"
          },
          {
            name: "Pertes",
            description: "Enregistrez les pertes de stock"
          }
        ]
      },
      {
        name: "Comptabilité",
        features: [
          {
            name: "Caisse",
            description: "Gérez votre caisse quotidienne"
          },
          {
            name: "Rapports",
            description: "Générez des rapports de ventes"
          }
        ]
      }
    ]
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <InterfaceHeader 
          title={interfaceModel?.business_types?.name || "Interface de gestion"}
          description={interfaceModel?.description || "Gérez votre entreprise"}
        />
        {businessTypeId && (
          <Button onClick={handleSetAsDefault} variant="outline">
            Définir comme modèle par défaut
          </Button>
        )}
      </div>

      <FeatureCards sections={commerceLayout.sections} />

      <ProductActionsSection 
        onAddProduct={() => setIsAddProductDialogOpen(true)}
        onDeleteProduct={() => setIsDeleteProductDialogOpen(true)}
      />

      <Card>
        <CardContent className="p-6">
          <ProductTable
            sampleData={localData}
            onSaleClick={onSaleClick}
            onRestock={onRestock}
            onLoss={onLoss}
            onDelete={onDelete}
            onReturn={onReturn}
          />
        </CardContent>
      </Card>

      <AddProductDialog
        isOpen={isAddProductDialogOpen}
        onOpenChange={setIsAddProductDialogOpen}
        onSubmit={handleAddProduct}
      />

      <DeleteProductDialog
        open={isDeleteProductDialogOpen}
        onOpenChange={setIsDeleteProductDialogOpen}
        stockItems={localData}
        onConfirmDelete={handleDeleteProduct}
      />

      <DialogManager
        sampleData={localData}
        setSampleData={setLocalData}
        isCredit={isCredit}
      />
    </div>
  );
};
